import { useCallback, useEffect, useMemo, useRef, type PointerEvent as ReactPointerEvent, type RefObject } from 'react';
import { EDITABLE_TYPES, type Geometry, type Item } from '../../doc/schema';
import { DEG, distance, normalizeRect, rectContains, rectsIntersect, rotatedBounds, type Point, type Rect } from '../../doc/geometry';
import { useBoard, usePaneStore, useReadOnly } from '../context';
import { commitTransient, createKanbanAt, createNoteAt, createRichTextAt, createShapeAt, createTableAt, createTitleAt, createTodoAt } from '../actions';
import { openAsset } from '../assets/localAssets';
import { openBoardCard } from '../boardCard';
import { cloneItems, copyItemsToBoard, createFrameAt } from '../actions';
import { IS_MAC } from '../../ui/platform';
import { withFrameContents } from '../frames';
import { paneAt } from '../paneRegistry';
import { exportRect } from '../export/exportActions';
import { useBoardMeta } from '../context';
import { importImageFiles } from '../importImages';
import { panBy, rotateAt, screenToWorld, snapRot, zoomAt } from './camera';
import { isRejectedTouch } from './DrawOverlay';
import { createSheetAt, enterPaint } from '../paint/session';
import { expandGroups } from '../arrange';
import { canSelect } from '../ink';
import { collectCandidates, snapRect, snapThreshold, type SnapCandidates } from '../selection/snapping';
import { toolSettings } from '../toolSettings';
import { rotatedBounds as bounds, unionRects, expandRect } from '../../doc/geometry';
import { visibleWorldRect } from './camera';

const DRAG_THRESHOLD_PX = 4;

type Gesture =
  | { kind: 'none' }
  | {
      kind: 'pending';
      pointerId: number;
      start: Point;
      startWorld: Point;
      itemId: string | null;
      additive: boolean;
      pointerType: string;
    }
  | { kind: 'pan'; pointerId: number; last: Point; /** a one-finger tap (no drag): selects the item under it, or clears the selection */ tap?: { start: Point; additive: boolean; itemId: string | null } }
  | { kind: 'marquee'; pointerId: number; startWorld: Point; base: ReadonlySet<string> }
  | {
      kind: 'move';
      pointerId: number;
      startWorld: Point;
      origins: Map<string, Geometry>;
      snap: SnapCandidates | null;
      startBounds: Rect | null;
      /** Alt-drag (Cmd on a Mac): the originals stay put and copies move. */
      duplicate: boolean;
    }
  | { kind: 'create'; pointerId: number; startWorld: Point; start: Point }
  | {
      kind: 'pinch';
      lastCenter: Point;
      lastDist: number;
      /** direction from one finger to the other, degrees */
      lastAngle: number;
      /** where the fingers have turned the canvas to; the camera shows it snapped to 45 degree steps */
      rawRot: number;
    };

/**
 * Pointer, wheel and drop handling for one viewport. Mouse: left drag = marquee / move,
 * middle or space+drag = pan, wheel = zoom, shift+wheel = pan, double-click = edit text / open link or board card.
 * Touch: one finger on empty = pan, on an item = move, two fingers = pinch zoom + pan + rotate
 * (the rotation snaps to 45 degree steps). A third finger pauses the pinch (three-finger tap = redo).
 * Creation tools: click (note, title, text) or drag (shape). Drawing is handled by DrawOverlay.
 */
export function useGestures(ref: RefObject<HTMLDivElement | null>) {
  const board = useBoard();
  const pane = usePaneStore();
  const readOnly = useReadOnly();
  const meta = useBoardMeta();
  const gesture = useRef<Gesture>({ kind: 'none' });
  const pointers = useRef(new Map<number, Point>());

  const toLocal = useCallback(
    (e: { clientX: number; clientY: number }): Point => {
      const el = ref.current;
      if (!el) return { x: e.clientX, y: e.clientY };
      const r = el.getBoundingClientRect();
      return { x: e.clientX - r.left, y: e.clientY - r.top };
    },
    [ref],
  );

  /** Drops whatever the current tool cannot select (ink outside the draw tool, and vice versa). */
  const selectable = useCallback(
    (ids: string[]): string[] => {
      const tool = pane.getState().tool;
      return ids.filter((id) => {
        const item = board.index.get(id);
        return !!item && canSelect(item, tool);
      });
    },
    [board, pane],
  );

  const abortGesture = useCallback(() => {
    const g = gesture.current;
    const s = pane.getState();
    if (g.kind === 'move') s.clearTransient();
    if (g.kind === 'marquee') s.setMarquee(null);
    if (g.kind === 'create') s.setCreating(null);
    gesture.current = { kind: 'none' };
  }, [pane]);

  /** Starts a pinch from the first two pointers, or takes a new reading of them mid-pinch. */
  const startPinch = useCallback(() => {
    const pts = [...pointers.current.values()];
    const a = pts[0];
    const b = pts[1];
    if (!a || !b) return;
    const g = gesture.current;
    gesture.current = {
      kind: 'pinch',
      lastCenter: { x: (a.x + b.x) / 2, y: (a.y + b.y) / 2 },
      lastDist: Math.max(1, distance(a, b)),
      lastAngle: Math.atan2(b.y - a.y, b.x - a.x) / DEG,
      rawRot: g.kind === 'pinch' ? g.rawRot : pane.getState().camera.rot,
    };
  }, [pane]);

  const onPointerDown = useCallback(
    (e: ReactPointerEvent<HTMLDivElement>) => {
      const el = ref.current;
      if (!el) return;
      const p = toLocal(e);
      pointers.current.set(e.pointerId, p);
      el.focus({ preventScroll: true });

      if (pointers.current.size === 2) {
        abortGesture();
        for (const id of pointers.current.keys()) el.setPointerCapture(id);
        startPinch();
        return;
      }
      if (pointers.current.size > 2) return;

      const s = pane.getState();
      const ctx = { board, pane };
      const itemEl = (e.target as Element).closest<HTMLElement>('[data-item-id]');
      const itemId = itemEl?.dataset.itemId ?? null;
      // Items without a focusable editor (kanban) leave edit mode when you press outside them.
      if (s.editingId && itemId !== s.editingId) s.setEditing(null);
      const isTouch = e.pointerType === 'touch';
      const middle = e.button === 1;

      // Palm rejection / pen-only apply to drawing only: a lone finger neither draws nor pans
      // while the draw tool is active. Pinch still works, and other tools take touch as usual.
      const painting = s.tool === 'sheet' && s.paintSheetId !== null;
      if (isTouch && (s.tool === 'draw' || s.tool === 'cutout' || painting) && isRejectedTouch(e.pointerType)) {
        gesture.current = { kind: 'none' };
        return;
      }
      if (e.pointerType === 'pen') isRejectedTouch('pen'); // records the contact time

      // Touch: a finger only moves items that are already selected. On empty canvas, or on an
      // item that is not selected yet, it pans; lifting without a drag is a tap that selects
      // (or deselects). This keeps a pan from grabbing whatever happens to be under the finger.
      const touchPan = isTouch && s.tool === 'select' && (!itemId || !s.selection.has(itemId));
      const wantPan =
        s.tool === 'pan' ||
        middle ||
        s.spaceHeld ||
        s.tool === 'draw' ||
        s.tool === 'cutout' || // pointers the draw and cutout overlays let through
        painting || // and the paint overlay (a press off every sheet)
        touchPan;

      if (wantPan) {
        el.setPointerCapture(e.pointerId);
        const tap = touchPan ? { start: p, additive: e.shiftKey, itemId } : undefined;
        gesture.current = { kind: 'pan', pointerId: e.pointerId, last: p, ...(tap ? { tap } : {}) };
        return;
      }
      if (e.button !== 0) return;

      const world = screenToWorld(s.camera, p);
      switch (readOnly ? 'select' : s.tool) {
        case 'note':
          createNoteAt(ctx, world);
          s.setTool('select');
          return;
        case 'title':
          createTitleAt(ctx, world);
          s.setTool('select');
          return;
        case 'richtext':
          createRichTextAt(ctx, world);
          s.setTool('select');
          return;
        case 'table':
          void createTableAt(ctx, world);
          s.setTool('select');
          return;
        case 'todo':
          void createTodoAt(ctx, world);
          s.setTool('select');
          return;
        case 'kanban':
          createKanbanAt(ctx, world);
          s.setTool('select');
          return;
        case 'sheet':
          // placing: a new sheet centred on the click, straight into paint mode
          createSheetAt(ctx, world);
          return;
        case 'shape':
        case 'frame':
        case 'exportArea':
          el.setPointerCapture(e.pointerId);
          gesture.current = { kind: 'create', pointerId: e.pointerId, startWorld: world, start: p };
          return;
      }

      const additive = e.shiftKey;
      if (itemId) {
        // Clicking a grouped item selects its whole group; Ctrl/Cmd+click picks the single item.
        const members = selectable(e.ctrlKey || e.metaKey ? [itemId] : expandGroups(ctx, [itemId]));
        if (additive) {
          if (s.selection.has(itemId)) s.select([...s.selection].filter((id) => !members.includes(id)));
          else s.addToSelection(members);
        } else if (!s.selection.has(itemId)) {
          s.select(members);
        }
      } else if (!additive) {
        s.clearSelection();
      }
      gesture.current = {
        kind: 'pending',
        pointerId: e.pointerId,
        start: p,
        startWorld: world,
        itemId,
        additive,
        pointerType: e.pointerType,
      };
    },
    [abortGesture, board, pane, readOnly, ref, selectable, startPinch, toLocal],
  );

  const onPointerMove = useCallback(
    (e: ReactPointerEvent<HTMLDivElement>) => {
      const p = toLocal(e);
      if (pointers.current.has(e.pointerId)) pointers.current.set(e.pointerId, p);
      const s = pane.getState();
      let g = gesture.current;

      if (g.kind === 'pinch') {
        const pts = [...pointers.current.values()];
        const a = pts[0];
        const b = pts[1];
        if (!a || !b) return;
        const center = { x: (a.x + b.x) / 2, y: (a.y + b.y) / 2 };
        const dist = Math.max(1, distance(a, b));
        const angle = Math.atan2(b.y - a.y, b.x - a.x) / DEG;
        // A third finger belongs to a three-finger tap (redo), not to the zoom.
        if (pointers.current.size === 2) {
          g.rawRot += ((angle - g.lastAngle + 540) % 360) - 180;
          let cam = zoomAt(s.camera, g.lastCenter, s.camera.zoom * (dist / g.lastDist));
          cam = rotateAt(cam, g.lastCenter, snapRot(g.rawRot));
          cam = panBy(cam, center.x - g.lastCenter.x, center.y - g.lastCenter.y);
          s.setCamera(cam);
        }
        g.lastCenter = center;
        g.lastDist = dist;
        g.lastAngle = angle;
        return;
      }

      if (g.kind === 'pan') {
        if (g.pointerId !== e.pointerId) return;
        s.setCamera(panBy(s.camera, p.x - g.last.x, p.y - g.last.y));
        g.last = p;
        return;
      }

      const world = screenToWorld(s.camera, p);

      if (g.kind === 'create') {
        if (g.pointerId !== e.pointerId) return;
        let rect = normalizeRect(g.startWorld, world);
        if (e.shiftKey) {
          const side = Math.max(rect.w, rect.h);
          rect = {
            x: world.x < g.startWorld.x ? g.startWorld.x - side : g.startWorld.x,
            y: world.y < g.startWorld.y ? g.startWorld.y - side : g.startWorld.y,
            w: side,
            h: side,
          };
        }
        s.setCreating(rect);
        return;
      }

      if (g.kind === 'pending') {
        if (g.pointerId !== e.pointerId) return;
        if (distance(p, g.start) < DRAG_THRESHOLD_PX) return;
        // Capture only now: capturing at pointerdown would retarget click/dblclick to the
        // viewport and items would never receive their double-clicks.
        ref.current?.setPointerCapture(e.pointerId);
        if (g.itemId) {
          const ids = new Set(s.selection);
          if (readOnly || !ids.has(g.itemId)) {
            gesture.current = { kind: 'none' };
            return;
          }
          const origins = new Map<string, Geometry>();
          const selected: Item[] = [];
          for (const id of ids) {
            const item = board.index.get(id);
            if (item && !board.isLocked(item)) selected.push(item);
          }
          // a frame carries whatever sits inside it
          let moving = withFrameContents(board, selected);
          // Alt-drag (Cmd-drag on a Mac) moves copies and leaves the originals where they are.
          // Only frames and grouped items chosen by the pointer count, the same set that moves.
          const duplicate = (IS_MAC ? e.metaKey : e.altKey) && moving.length > 0;
          if (duplicate) {
            const copies = cloneItems(board, moving, 0, 0);
            // the copies of what was selected become the selection; the originals stay as snap targets
            const wasSelected = new Set(selected.map((i) => i.id));
            s.select(copies.filter((_, i) => wasSelected.has(moving[i]!.id)));
            moving = copies.map((id) => board.index.get(id)).filter((i): i is Item => !!i);
            ids.clear();
          }
          for (const item of moving) {
            origins.set(item.id, item);
            ids.add(item.id);
          }
          const wantSnap = toolSettings.getState().snap;
          const area = expandRect(visibleWorldRect(s.camera, s.size), 0.5);
          g = {
            kind: 'move',
            pointerId: e.pointerId,
            startWorld: g.startWorld,
            origins,
            snap: wantSnap ? collectCandidates(board.index, area, ids) : null,
            startBounds: unionRects(moving.map(bounds)),
            duplicate,
          };
        } else {
          g = {
            kind: 'marquee',
            pointerId: e.pointerId,
            startWorld: g.startWorld,
            base: g.additive ? s.selection : new Set(),
          };
        }
        gesture.current = g;
      }

      if (g.kind === 'move') {
        if (g.pointerId !== e.pointerId) return;
        let dx = world.x - g.startWorld.x;
        let dy = world.y - g.startWorld.y;
        // Alt bypasses snapping, except while it is the key that made this a duplicating drag
        if (g.snap && g.startBounds && !(e.altKey && !(g.duplicate && !IS_MAC))) {
          const moved = { x: g.startBounds.x + dx, y: g.startBounds.y + dy, w: g.startBounds.w, h: g.startBounds.h };
          const ts = toolSettings.getState();
          const gridSize = (board.meta.get('gridSize') as number | undefined) ?? 32;
          const r = snapRect(moved, g.snap, snapThreshold(s.camera.zoom), ts.snapGrid ? gridSize : null);
          dx += r.dx;
          dy += r.dy;
          s.setSnapGuides({ x: r.gx, y: r.gy });
        } else {
          s.setSnapGuides({ x: null, y: null });
        }
        const t: Record<string, Partial<Geometry>> = {};
        for (const [id, o] of g.origins) t[id] = { x: o.x + dx, y: o.y + dy };
        s.setTransient(t);
        return;
      }

      if (g.kind === 'marquee') {
        if (g.pointerId !== e.pointerId) return;
        const rect = normalizeRect(g.startWorld, world);
        s.setMarquee(rect);
        const hits = board.index
          .query(rect)
          .filter(
            (i) =>
              canSelect(i, s.tool) &&
              !board.isLocked(i) &&
              !board.isHidden(i) &&
              (i.type === 'frame' ? rectContains(rect, rotatedBounds(i)) : rectsIntersect(rotatedBounds(i), rect)),
          )
          .map((i) => i.id);
        s.select([...g.base, ...selectable(expandGroups({ board, pane }, hits))]);
      }
    },
    [board, pane, readOnly, ref, selectable, toLocal],
  );

  const endPointer = useCallback(
    (e: ReactPointerEvent<HTMLDivElement>) => {
      pointers.current.delete(e.pointerId);
      const el = ref.current;
      if (el?.hasPointerCapture(e.pointerId)) el.releasePointerCapture(e.pointerId);
      const g = gesture.current;
      const s = pane.getState();

      if (g.kind === 'pinch') {
        if (pointers.current.size >= 2) {
          startPinch(); // the first two pointers may be a different pair now
          return;
        }
        const remaining = [...pointers.current.entries()][0];
        gesture.current = remaining
          ? { kind: 'pan', pointerId: remaining[0], last: remaining[1] }
          : { kind: 'none' };
        return;
      }
      if ('pointerId' in g && g.pointerId !== e.pointerId) return;

      if (g.kind === 'pan') {
        if (g.tap && distance(toLocal(e), g.tap.start) < DRAG_THRESHOLD_PX) {
          if (g.tap.itemId) {
            const members = selectable(expandGroups({ board, pane }, [g.tap.itemId]));
            if (g.tap.additive) s.addToSelection(members);
            else s.select(members);
          } else if (!g.tap.additive) {
            s.clearSelection();
          }
        }
      } else if (g.kind === 'move') {
        s.setSnapGuides({ x: null, y: null });
        // Dropped onto another pane (split view): move there, or copy when it is another board.
        const target = paneAt(e.clientX, e.clientY);
        if (target && target.pane !== pane && g.origins.size) {
          const vr = target.el.querySelector('.viewport')?.getBoundingClientRect();
          const local = vr ? { x: e.clientX - vr.left, y: e.clientY - vr.top } : { x: 0, y: 0 };
          const world = screenToWorld(target.pane.getState().camera, local);
          const positions = new Map<string, Point>();
          for (const [id, o] of g.origins) positions.set(id, { x: world.x - (g.startWorld.x - o.x), y: world.y - (g.startWorld.y - o.y) });
          s.clearTransient();
          if (target.board === board) {
            board.updateItems([...positions].map(([id, p]) => [id, { x: p.x, y: p.y }]));
            target.pane.getState().select(positions.keys());
          } else {
            // a duplicating drag into another board: the copies made here have nothing to do
            const leftovers = g.duplicate ? [...g.origins.keys()] : [];
            void copyItemsToBoard(board, target.board, positions).then((ids) => {
              target.pane.getState().select(ids);
              if (leftovers.length) board.deleteItems(leftovers);
            });
            if (leftovers.length) s.clearSelection();
          }
          gesture.current = { kind: 'none' };
          return;
        }
        commitTransient({ board, pane });
      } else if (g.kind === 'marquee') {
        s.setMarquee(null);
      } else if (g.kind === 'create') {
        const p = toLocal(e);
        const world = screenToWorld(s.camera, p);
        const rect = s.creating;
        s.setCreating(null);
        const dragged = distance(p, g.start) >= DRAG_THRESHOLD_PX && rect && rect.w > 2 && rect.h > 2;
        if (s.tool === 'exportArea') {
          if (dragged && rect) void exportRect(board, meta.title, rect, 'area');
        } else if (s.tool === 'frame') {
          createFrameAt({ board, pane }, dragged && rect ? rect : g.startWorld);
        } else {
          createShapeAt({ board, pane }, dragged ? rect : g.startWorld, {
            flip: world.y < g.startWorld.y !== world.x < g.startWorld.x,
          });
        }
        s.setTool('select');
      }
      gesture.current = { kind: 'none' };
    },
    [board, meta.title, pane, ref, selectable, startPinch, toLocal],
  );

  const onDoubleClick = useCallback(
    (e: React.MouseEvent<HTMLDivElement>) => {
      const s = pane.getState();
      if (s.tool !== 'select') return;
      let target = e.target as Element;
      if (readOnly) {
        // viewers may still open links and download attachments
        const el = target.closest<HTMLElement>('[data-item-id]');
        const it = el ? board.index.get(el.dataset.itemId ?? '') : undefined;
        if (it?.type === 'link') window.open(it.props.url, '_blank', 'noopener,noreferrer');
        else if (it?.type === 'file') void openAsset(it.props.assetId, it.props.name);
        else if (it?.type === 'boardcard') void openBoardCard(board, it);
        return;
      }
      // If some capture path still retargeted the event to the viewport, hit-test manually.
      if (target === ref.current) target = document.elementFromPoint(e.clientX, e.clientY) ?? target;
      if (target.closest('.sel-box')) return; // handles are not canvas
      const itemEl = target.closest<HTMLElement>('[data-item-id]');
      if (itemEl) {
        const item = board.index.get(itemEl.dataset.itemId ?? '');
        if (!item || board.isLocked(item)) return;
        if (EDITABLE_TYPES.has(item.type)) {
          s.select([item.id]);
          s.setEditing(item.id);
        } else if (item.type === 'sheet') {
          enterPaint({ board, pane }, item.id);
        } else if (item.type === 'link') {
          window.open(item.props.url, '_blank', 'noopener,noreferrer');
        } else if (item.type === 'file') {
          void openAsset(item.props.assetId, item.props.name);
        } else if (item.type === 'boardcard') {
          void openBoardCard(board, item);
        }
      }
      // Empty canvas: nothing. Notes are created with the note tool only.
    },
    [board, pane, readOnly, ref],
  );

  // Wheel = zoom at the cursor (trackpad pinch arrives as ctrl+wheel and zooms too).
  // Shift+wheel pans; so does a trackpad's horizontal delta. Non-passive so the page never scrolls.
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const onWheel = (e: WheelEvent) => {
      e.preventDefault();
      const s = pane.getState();
      const mul = e.deltaMode === 1 ? 16 : e.deltaMode === 2 ? 100 : 1;
      const dx = e.deltaX * mul;
      const dy = e.deltaY * mul;
      if (e.shiftKey) {
        s.setCamera(panBy(s.camera, dx !== 0 ? -dx : -dy, dx !== 0 ? -dy : 0));
        return;
      }
      if (!e.ctrlKey && !e.metaKey && dx !== 0 && Math.abs(dx) > Math.abs(dy)) {
        s.setCamera(panBy(s.camera, -dx, -dy));
        return;
      }
      const factor = Math.exp(-dy * (e.ctrlKey || e.metaKey ? 0.01 : 0.0025));
      s.setCamera(zoomAt(s.camera, toLocal(e), s.camera.zoom * factor));
    };
    el.addEventListener('wheel', onWheel, { passive: false });
    return () => el.removeEventListener('wheel', onWheel);
  }, [pane, ref, toLocal]);

  const dropHandlers = useMemo(
    () => ({
      onDragOver: (e: React.DragEvent) => {
        if (readOnly || !e.dataTransfer.types.includes('Files')) return;
        e.preventDefault();
        e.dataTransfer.dropEffect = 'copy';
        pane.getState().setDragOver(true);
      },
      onDragLeave: () => pane.getState().setDragOver(false),
      onDrop: (e: React.DragEvent) => {
        e.preventDefault();
        pane.getState().setDragOver(false);
        if (readOnly) return;
        const files = Array.from(e.dataTransfer.files);
        if (!files.length) return;
        const world = screenToWorld(pane.getState().camera, toLocal(e));
        void importImageFiles({ board, pane }, files, world);
      },
    }),
    [board, pane, readOnly, toLocal],
  );

  return {
    onPointerDown,
    onPointerMove,
    onPointerUp: endPointer,
    onPointerCancel: endPointer,
    onDoubleClick,
    ...dropHandlers,
  };
}
